import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import brandLogo from "../../template/images/Finaware.svg";
// import dspFavicon from "../../template/images/dspFavicon.png";
import dspFavicon from "../../template/images/favIcon.png";
import HighContrast from "../../template/images/HighContrast.svg";
import BigCursor from "../../template/images/BigCursor.svg";
import HighlightLinks from "../../template/images/HighlightLinks.svg";
import Invert from "../../template/images/Invert.svg";
import NormalContrast from "../../template/images/NormalContrast.svg";
import Saturation from "../../template/images/Saturation.svg";
import "../layout/accessibility.css";
import "./EventsLanding.css";
export default function DspHeader() {
  const navigate = useNavigate();

  // Accessibility state and ref
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const accessibilityRef = useRef(null);

  const [activeAccessibility, setActiveAccessibility] = useState(() => {
    const savedSettings = localStorage.getItem("accessibilitySettings");
    return savedSettings
      ? JSON.parse(savedSettings)
      : {
          contrast: false,
          spacing: false,
          cursor: false,
          lineHeight: false,
          hideImage: false,
          highlightLinks: false,
          saturation: false,
          invert: false,
        };
  });

  // Font size management - Simple 3-level system
  // Level -1 = decrease by 2px, 0 = normal, 1 = increase by 2px
  const getInitialFontLevel = () => {
    const savedLevel = localStorage.getItem("fontLevel");
    return savedLevel ? parseInt(savedLevel, 10) : 0;
  };

  const [fontLevel, setFontLevel] = useState(getInitialFontLevel);

  // Set favicon to LogoDSP.svg
  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }
    link.href = dspFavicon;
  }, []);

  const toggleAccessibility = () => {
    setAccessibilityOpen((prev) => !prev);
  };

  const toggleBodyClass = (key, className) => {
    const isActive = activeAccessibility[key];

    if (isActive) {
      document.body.classList.remove(className);
    } else {
      document.body.classList.add(className);
    }

    const newSettings = {
      ...activeAccessibility,
      [key]: !isActive,
    };

    setActiveAccessibility(newSettings);
    localStorage.setItem("accessibilitySettings", JSON.stringify(newSettings));
  };

  const setBodyClass = (key, className, shouldAdd) => {
    console.log(
      `setBodyClass called: key=${key}, className=${className}, shouldAdd=${shouldAdd}`,
    );

    if (shouldAdd) {
      document.body.classList.add(className);
      console.log(`Added class "${className}" to body`);
    } else {
      document.body.classList.remove(className);
      console.log(`Removed class "${className}" from body`);
    }

    const newSettings = {
      ...activeAccessibility,
      [key]: shouldAdd,
    };

    setActiveAccessibility(newSettings);
    localStorage.setItem("accessibilitySettings", JSON.stringify(newSettings));
    console.log("Updated settings:", newSettings);
    console.log("Body classes:", document.body.className);
  };

  const handleClickOutside = useCallback((event) => {
    if (
      accessibilityRef.current &&
      !accessibilityRef.current.contains(event.target)
    ) {
      setAccessibilityOpen(false);
    }
  }, []);

  // Font size handlers - Simple 3-level system
  const handleIncrease = () => {
    setFontLevel(1); // Increase by 2px (A+)
  };

  const handleDecrease = () => {
    setFontLevel(-1); // Decrease by 2px (A-)
  };

  const handleNormalFont = () => {
    setFontLevel(0); // Reset to standard size (A)
  };

  // Apply font size changes based on level
  useEffect(() => {
    const getComputedFontSize = () => {
      return window.getComputedStyle(document.documentElement).fontSize;
    };

    const fontSizeBefore = getComputedFontSize();

    // Remove all font size classes from <html> element
    document.documentElement.classList.remove("font-decrease", "font-increase");

    // Apply the current level class to <html> for global proportional scaling
    let levelName = "NORMAL (A)";
    if (fontLevel === -1) {
      document.documentElement.classList.add("font-decrease");
      levelName = "DECREASED (A-)";
    } else if (fontLevel === 1) {
      document.documentElement.classList.add("font-increase");
      levelName = "INCREASED (A+)";
    }

    const fontSizeAfter = getComputedFontSize();

    // Debug logging
    console.group("🔤 Font Size Change");
    console.log(`📊 Level: ${levelName}`);
    console.log(`📏 HTML Font Size Before: ${fontSizeBefore}`);
    console.log(`📏 HTML Font Size After: ${fontSizeAfter}`);
    console.log(`📋 HTML Classes: ${document.documentElement.className}`);
    console.log(`✅ Change Applied: ${fontSizeBefore !== fontSizeAfter}`);
    console.groupEnd();

    localStorage.setItem("fontLevel", fontLevel.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontLevel]);

  useEffect(() => {
    if (accessibilityOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [accessibilityOpen, handleClickOutside]);

  // Apply saved accessibility settings on mount
  useEffect(() => {
    console.log(
      "Applying saved accessibility settings on mount:",
      activeAccessibility,
    );
    Object.entries(activeAccessibility).forEach(([key, isActive]) => {
      const className = {
        contrast: "contrast",
        spacing: "addLetterSpacing",
        cursor: "big_cursor",
        lineHeight: "line_Height",
        hideImage: "hide_Image",
        highlightLinks: "highlight_Links",
        saturation: "saturation",
        invert: "invert",
      }[key];

      if (isActive && className) {
        document.body.classList.add(className);
        console.log(`Applied saved setting: ${key} -> ${className}`);
      }
    });
    console.log("Final body classes after mount:", document.body.className);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <header className="events-header">
      <div className="container-lg">
        <div className="header-content">
          <div className="logo-section">
            <div
              className="dsp-logo"
              onClick={() => navigate("/")}
              style={{ cursor: "pointer" }}
            >
              <img src={brandLogo} alt="Finaware logo" className="img-fluid" />
            </div>
          </div>
          <div className="header-actions">
            <div
              className="accessibility-wrapper"
              ref={accessibilityRef}
              style={{ position: "relative" }}
            >
              <button
                className="accessibility-btn"
                aria-label="Accessibility"
                onClick={toggleAccessibility}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                >
                  <path
                    d="M12.3629 0C15.6418 0 18.7864 1.30252 21.1048 3.62102C23.4233 5.93952 24.7259 9.08408 24.7259 12.3629C24.7259 15.6418 23.4233 18.7864 21.1048 21.1048C18.7864 23.4233 15.6418 24.7259 12.3629 24.7259C9.08408 24.7259 5.93952 23.4233 3.62102 21.1048C1.30252 18.7864 0 15.6418 0 12.3629C0 9.08408 1.30252 5.93952 3.62102 3.62102C5.93952 1.30252 9.08408 0 12.3629 0ZM12.3629 1.77333C10.9462 1.73126 9.53545 1.97397 8.21423 2.48708C6.89302 3.00019 5.68823 3.77327 4.67125 4.7605C3.65426 5.74773 2.84577 6.92904 2.29367 8.23445C1.74157 9.53985 1.45709 10.9428 1.45709 12.3601C1.45709 13.7775 1.74157 15.1804 2.29367 16.4858C2.84577 17.7912 3.65426 18.9725 4.67125 19.9598C5.68823 20.947 6.89302 21.7201 8.21423 22.2332C9.53545 22.7463 10.9462 22.989 12.3629 22.9469C15.1291 22.8828 17.7603 21.7388 19.6938 19.7597C21.6273 17.7805 22.7095 15.1233 22.709 12.3564C22.7085 9.58958 21.6253 6.93276 19.6912 4.95427C17.757 2.97579 15.1253 1.83273 12.3592 1.7696M17.8715 8.27307C17.9912 8.23635 18.1171 8.22412 18.2417 8.2371C18.3663 8.25008 18.487 8.288 18.5966 8.34862C18.7063 8.40923 18.8026 8.49128 18.8798 8.58989C18.957 8.6885 19.0136 8.80165 19.0462 8.9226C19.0788 9.04355 19.0867 9.16982 19.0695 9.29389C19.0522 9.41795 19.0102 9.53728 18.9459 9.64476C18.8815 9.75224 18.7962 9.84567 18.695 9.91948C18.5938 9.99329 18.4788 10.046 18.3568 10.0744L14.6851 11.0581V13.8003C14.6851 14.924 16.3277 20.1973 16.3277 20.1973C16.3596 20.3157 16.3678 20.4393 16.352 20.5608C16.3361 20.6824 16.2964 20.7997 16.2353 20.9059C16.1117 21.1205 15.9079 21.2772 15.6688 21.3416C15.4297 21.406 15.1748 21.3727 14.9602 21.2491C14.7456 21.1255 14.5889 20.9218 14.5245 20.6827C14.5245 20.6827 13.1469 15.3981 13.0499 15.1629C12.9603 14.9259 12.6429 14.9259 12.6429 14.9259H12.0699C12.0587 14.9259 11.7451 14.9296 11.6611 15.1629C11.5584 15.4037 10.1901 20.6509 10.1808 20.6864C10.1491 20.8048 10.0943 20.9158 10.0197 21.013C9.94502 21.1103 9.85197 21.1919 9.7458 21.2531C9.63963 21.3144 9.52243 21.3542 9.4009 21.3701C9.27936 21.3861 9.15587 21.378 9.03747 21.3463C8.91907 21.3145 8.80808 21.2598 8.71084 21.1851C8.6136 21.1105 8.53202 21.0174 8.47074 20.9113C8.40947 20.8051 8.36971 20.6879 8.35372 20.5664C8.33774 20.4448 8.34586 20.3213 8.3776 20.2029C8.3888 20.1712 10.0221 14.9203 10.0221 13.8003V11.0563L6.36533 10.0781C6.13397 10.0072 5.93919 9.84919 5.8221 9.63741C5.705 9.42563 5.67474 9.17664 5.7377 8.94298C5.80065 8.70932 5.9519 8.50924 6.15954 8.38496C6.36719 8.26067 6.61498 8.22189 6.85067 8.2768C6.85067 8.2768 9.93814 9.32587 11.1421 9.32587H13.5875C14.7859 9.32587 17.8472 8.28613 17.8752 8.2768M12.3629 3.72027C12.9199 3.72027 13.454 3.94152 13.8479 4.33534C14.2417 4.72917 14.4629 5.26331 14.4629 5.82027C14.4629 6.37722 14.2417 6.91137 13.8479 7.30519C13.454 7.69902 12.9199 7.92027 12.3629 7.92027C11.806 7.92027 11.2718 7.69902 10.878 7.30519C10.4842 6.91137 10.2629 6.37722 10.2629 5.82027C10.2629 5.26331 10.4842 4.72917 10.878 4.33534C11.2718 3.94152 11.806 3.72027 12.3629 3.72027Z"
                    fill="#0CF9E0"
                  />
                </svg>
              </button>

              {accessibilityOpen && (
                <div className="accessibility-dropdown">
                  <h5 className="accessibility-heading">Accessibility Tools</h5>
                  <div className="acess-item">
                    <div className="access-item-title">Color Contrast</div>
                    <div className="access-item-row">
                      <button
                        className={activeAccessibility.contrast ? "active" : ""}
                        onClick={() =>
                          setBodyClass("contrast", "contrast", true)
                        }
                      >
                        <img
                          src={HighContrast}
                          alt="High Contrast Icon"
                          style={{
                            background: "#F3F4F6",
                            borderRadius: "8px",
                            padding: "6px",
                          }}
                        />
                        <span>High Contrast</span>
                      </button>
                      <button
                        className={
                          !activeAccessibility.contrast ? "active" : ""
                        }
                        onClick={() =>
                          setBodyClass("contrast", "contrast", false)
                        }
                      >
                        <img
                          src={NormalContrast}
                          alt="Normal Contrast Icon"
                          style={{
                            background: "#F3F4F6",
                            borderRadius: "8px",
                            padding: "6px",
                          }}
                        />
                        <span>Normal Contrast</span>
                      </button>

                      <button
                        className={
                          activeAccessibility.highlightLinks ? "active" : ""
                        }
                        onClick={() =>
                          toggleBodyClass("highlightLinks", "highlight_Links")
                        }
                      >
                        <img
                          src={HighlightLinks}
                          alt="Highlight Links Icon"
                          style={{
                            background: "#F3F4F6",
                            borderRadius: "8px",
                            padding: "6px",
                          }}
                        />
                        <span>Highlight Links</span>
                      </button>
                      <button
                        className={
                          activeAccessibility.saturation ? "active" : ""
                        }
                        style={{ minWidth: "180px" }}
                        onClick={() =>
                          toggleBodyClass("saturation", "saturation")
                        }
                      >
                        <img
                          src={Saturation}
                          alt="Saturation Icon"
                          style={{
                            background: "#F3F4F6",
                            borderRadius: "8px",
                            padding: "6px",
                          }}
                        />
                        <span>Saturation</span>
                      </button>
                      <button
                        className={activeAccessibility.invert ? "active" : ""}
                        style={{ minWidth: "180px" }}
                        onClick={() => toggleBodyClass("invert", "invert")}
                      >
                        <img
                          src={Invert}
                          alt="Invert Colors Icon"
                          style={{
                            background: "#F3F4F6",
                            borderRadius: "8px",
                            padding: "6px",
                          }}
                        />
                        <span>Invert</span>
                      </button>
                    </div>
                  </div>

                  <div className="acess-item">
                    <h4>Text Size</h4>
                    <div className="access-item-row">
                      <button
                        onClick={handleIncrease}
                        className={fontLevel === 1 ? "active" : ""}
                        title="Increase font size by 2px"
                      >
                        <h3>
                          <strong style={{ fontSize: "20px" }}>A+</strong>
                        </h3>
                        <span>Font Increase</span>
                      </button>
                      <button
                        onClick={handleDecrease}
                        className={fontLevel === -1 ? "active" : ""}
                        title="Decrease font size by 2px"
                      >
                        <h2>
                          <strong style={{ fontSize: "20px" }}>A-</strong>
                        </h2>
                        <span>Font Decrease</span>
                      </button>
                      <button
                        onClick={handleNormalFont}
                        className={fontLevel === 0 ? "active" : ""}
                        title="Reset to standard font size"
                      >
                        <h3>
                          <strong style={{ fontSize: "20px" }}>A</strong>
                        </h3>
                        <span>Normal Font</span>
                      </button>
                    </div>
                  </div>

                  <div className="acess-item">
                    <h4>Spacing</h4>
                    <div className="access-item-row">
                      <button
                        className={activeAccessibility.spacing ? "active" : ""}
                        style={{ minWidth: "180px" }}
                        onClick={() =>
                          toggleBodyClass("spacing", "addLetterSpacing")
                        }
                      >
                        <h3>
                          <strong style={{ fontSize: "20px" }}>T</strong>
                        </h3>
                        <span>Text Spacing</span>
                      </button>
                      <button
                        className={
                          activeAccessibility.lineHeight ? "active" : ""
                        }
                        style={{ minWidth: "180px" }}
                        onClick={() =>
                          toggleBodyClass("lineHeight", "line_Height")
                        }
                      >
                        <h3>
                          <strong style={{ fontSize: "20px" }}>TI</strong>
                        </h3>
                        <span>Line Height</span>
                      </button>
                    </div>
                  </div>

                  <div className="acess-item">
                    <h4>Visual</h4>
                    <div className="access-item-row">
                      <button
                        className={activeAccessibility.cursor ? "active" : ""}
                        style={{ minWidth: "180px" }}
                        onClick={() => toggleBodyClass("cursor", "big_cursor")}
                      >
                        <img
                          src={BigCursor}
                          alt="Big Cursor Icon"
                          style={{
                            background: "#F3F4F6",
                            borderRadius: "8px",
                            padding: "6px",
                          }}
                        />
                        <span>Big Cursor</span>
                      </button>
                      <button
                        className={
                          activeAccessibility.hideImage ? "active" : ""
                        }
                        style={{ minWidth: "180px" }}
                        onClick={() =>
                          toggleBodyClass("hideImage", "hide_Image")
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                          style={{
                            background: "#F3F4F6",
                            borderRadius: "8px",
                            padding: "6px",
                          }}
                        >
                          <path d="M792.92-93.85 677.38-209.39H172.31q-27.01 0-45.66-18.65Q108-246.69 108-273.7v-412.6q0-27.7 18.16-46.35 18.15-18.66 44.84-18.66h21.54l36.15 36.16H175l-93.15-93.16L110-836.46l711.08 711.07-28.16 28.16ZM823.39-316l-40-40v-331.69q0-5.39-3.47-8.85-3.46-3.46-8.84-3.46H438.85l-60-60h408.84q27.01 0 45.66 18.65Q852-722.7 852-695.69v412.6q0 9.24-2.69 17.7-2.7 8.47-8.08 14.85l-17.84-65.46ZM269.23-290h348.15l-102-102-49.61 66-93.85-123.85L269.23-290Zm221.08-266.31ZM404-384.77Z"></path>
                        </svg>
                        <span>Hide Images</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
